/**
 * RimSubMenu — Rim set picker panel.
 *
 * Renders inside <SubMenuPanel> when activeSubMenu === 'rim'.
 *
 * Visual spec (08-ui-screens.md — Color/Rim SubMenu):
 *   • Same layout rhythm as ColorSubMenu — horizontal scroll, fade masks,
 *     40px round buttons, white outline ring on selection
 *   • Each button shows the rim's `icon` (from model config). Falls back to
 *     an inline SVG wheel glyph when no icon is configured.
 *   • No background on buttons — transparent, icon only — matching the
 *     color swatch aesthetic for visual consistency
 *   • "Loading…" hint above the scroll track while a rim swap is in progress
 *   • Rim display name label below — fades on change
 *
 * On tap:
 *   1. Store: setSelectedRim(id) → triggers useRim → engine.showAsset()
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#color--rim-submenu
 * @see packages/brand-audi/src/hooks/useRim.ts
 */
import { memo, useState } from 'react';
import { cn } from '../../../utils/cn';
import { useAppStore } from '../../../store/app.store';
import { tracker, EVENTS } from '../../../services/tracking';
import type { ResolvedRimSet } from '@ims/shared';

interface RimSubMenuProps {
  rims: ResolvedRimSet[];
  defaultRimId: string;
}

export const RimSubMenu = memo(function RimSubMenu({ rims, defaultRimId }: RimSubMenuProps) {
  const selectedRimId = useAppStore((s) => s.selectedRimId);
  const setSelectedRim = useAppStore((s) => s.setSelectedRim);

  const activeId = selectedRimId ?? defaultRimId;

  // Derive display name directly from rims + activeId — stays correct even
  // if the rims prop or defaultRimId changes (e.g. model switch).
  const displayName =
    rims.find((r) => r.id === activeId)?.displayName ?? rims[0]?.displayName ?? '';

  const [nameFadeKey, setNameFadeKey] = useState(0);

  function handleSelect(id: string) {
    if (id === activeId) return;
    setSelectedRim(id);
    tracker.track(EVENTS.RIM_CHANGED, { rimId: id });
    setNameFadeKey((k) => k + 1);
  }

  if (!rims.length) return null;

  return (
    <div className="flex flex-col items-center gap-0 pb-4">
      {/* ── Rim button scroll track ──────────────────────────────────────── */}
      <div
        className="relative w-full"
        style={{
          maskImage:
            'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
        }}
      >
        <div
          className="flex items-center gap-8 overflow-x-auto scrollbar-hide w-full py-3"
          style={{
            justifyContent: 'safe center',
            WebkitOverflowScrolling: 'touch' as React.CSSProperties['WebkitOverflowScrolling'],
            overscrollBehaviorX: 'contain',
          }}
        >
          {/* Leading spacer */}
          <div className="shrink-0 w-6" aria-hidden />

          {rims
            .filter((rim) => !rim.hidden)
            .map((rim) => {
              const isActive = rim.id === activeId;

              return (
                <button
                  key={rim.id}
                  onClick={() => handleSelect(rim.id)}
                  aria-label={rim.displayName}
                  aria-pressed={isActive}
                  className={cn(
                    // Matches ColorSubMenu exactly: 40px round, dark bg, drop shadow
                    'relative shrink-0 w-10 h-10 rounded-full cursor-pointer',
                    'transition-all duration-200',
                    'bg-transparent',
                  )}
                  style={{
                    ...(isActive
                      ? {
                          outline: '2px solid rgba(255,255,255,0.85)',
                          outlineOffset: '2px',
                          boxShadow: '0 0 8px rgba(255,255,255,0.4), 0 2px 8px rgba(0,0,0,0.5)',
                        }
                      : {}),
                  }}
                >
                  {rim.icon ? (
                    <img
                      src={rim.icon}
                      alt=""
                      aria-hidden
                      draggable={false}
                      className="absolute inset-0 w-full h-full rounded-full object-cover pointer-events-none select-none"
                    />
                  ) : (
                    /* Fallback: inline SVG wheel glyph */
                    <svg
                      aria-hidden
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className="absolute inset-0 w-full h-full p-2 text-white/70"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <circle cx="12" cy="12" r="3.5" />
                      <line x1="12" y1="3" x2="12" y2="8.5" />
                      <line x1="12" y1="15.5" x2="12" y2="21" />
                      <line x1="3" y1="12" x2="8.5" y2="12" />
                      <line x1="15.5" y1="12" x2="21" y2="12" />
                    </svg>
                  )}
                </button>
              );
            })}

          {/* Trailing spacer */}
          <div className="shrink-0 w-6" aria-hidden />
        </div>
      </div>

      {/* ── Active rim label ─────────────────────────────────────────────── */}
      <p
        key={nameFadeKey}
        className="text-sm font-normal text-white text-center tracking-wide"
        style={{
          animation: 'rimLabelFadeIn 200ms ease forwards',
        }}
      >
        {displayName}
      </p>

      <style>{`
        @keyframes rimLabelFadeIn {
          from { opacity: 0; transform: translateY(3px); }
          to   { opacity: 1; transform: translateY(0); }
        }

      `}</style>
    </div>
  );
});
